/**
 * Shared curriculum/module view types. Fields are widened to `string` (rather
 * than the branded SubjectType/Class/ProgrammeType unions the apps use) so both
 * admin-console and teacher-app can pass their own domain objects in without
 * coupling to either app's type module.
 */

export type BookLevel = 'unit' | 'chapter' | 'section' | 'subsection';
export type ModuleLevel = 'module' | 'chapter' | 'topic';
export type PlacementNodeKind = 'module' | 'chapter' | 'topic' | 'concept';

/** A canonical taxonomy node (module → chapter → topic). */
export interface CanonModule {
  id: string;
  displayName: string;
  subject: string;
  grades?: string[];
  // Per-board grade for this topic, grouped by grade: [grade, exams[]]. Authoritative
  // for grade-per-board. See docs/FeatureDesigns/question-pools-and-entitlement/question-grade-board-model-vision.md.
  gradeBoards?: [string, string[]][];
  sortOrder?: number;
  isActive?: boolean;
  moduleLevel?: ModuleLevel;
  isTopModule?: boolean;
  parentModuleId?: string;
  readableId?: string;
  questionCount?: number;
  /** Teacher-authored questions on this module, scoped by the server to the
   *  caller: every teacher's for an admin, only their own for a teacher. */
  teacherQuestionCount?: number;
}

/** One syllabus = (board/exam, subject, grade). */
export interface CurriculumInfo {
  id: string;
  name: string;
  board: string;
  subject: string;
  grade?: string;
  examTrack?: boolean;
  /** Active in the app (default true). Inactive curricula are hidden from
   *  active-curriculum consumers (plan blueprint, exam-goal decomposition). */
  isActive?: boolean;
  /** undefined/'admin' = admin-authored public; 'teacher' = private to `teacherId`. */
  ownerType?: 'admin' | 'teacher';
  teacherId?: string;
}

/** List-row augmentation returned by listCurricula. */
export interface CurriculumListItem extends CurriculumInfo {
  counts?: { unit: number; chapter: number; section: number; subsection: number };
  lastModified?: number;
  // In-scope question count (curriculum stats' totalInScope). Absent until first opened.
  questionCount?: number;
  // Lost-question coverage stat, computed on last curriculum open. Absent until first opened.
  coverage?: { lostPct: number; lostCount: number; total: number; computedAt: string };
  /** Chapters with too few questions to practise from. `threshold` is the value
   *  the stat was computed under, so the warning can state it rather than guess.
   *  Absent until the curriculum is first opened. */
  thinChapters?: { count: number; total: number; threshold: number };
}

/** A book-ToC entry that optionally maps to a canonical node. */
export interface CurriculumPlacement {
  id: string;
  curriculumId?: string;
  parentId?: string;
  bookLevel: BookLevel;
  number: string;
  displayName: string;
  order?: number;
  /** Canonical modules this book node maps to; [0] = primary (identity), rest = bundled topics. */
  canonicalNodeIds?: string[];
  /** Pages this chapter spans in the prescribed book — the observable effort derives from. */
  pageCount?: number;
  /** How dense those pages are. Absent reads as `medium`. */
  complexityFactor?: ComplexityFactor;
  /** Server-derived hours for a first pass; display only, never sent back. */
  firstPassHours?: number | null;
}

/** Authored chapter density. Numeric multipliers live server-side, not here. */
export type ComplexityFactor = 'very-light' | 'light' | 'medium' | 'dense' | 'very-dense';

/**
 * Client-side echo of the server's effort formula, for previews and table cells.
 *
 * Duplicated from shared/curriculum/chapterEffort.ts deliberately and kept
 * trivial: the alternative is a round-trip per keystroke to show one number. The
 * server remains the authority — its value returns as `firstPassHours` on the
 * next load, and if the two disagree the server wins. A change to the real
 * formula belongs here too.
 */
const COMPLEXITY_MULT: Record<ComplexityFactor, number> = {
  'very-light': 0.5, light: 0.75, medium: 1, dense: 1.5, 'very-dense': 2,
};
const BASE_MINUTES_PER_PAGE = 8;

export function estimateFirstPassHours(pages: number, cx?: ComplexityFactor): number {
  return Math.round((pages * BASE_MINUTES_PER_PAGE * COMPLEXITY_MULT[cx ?? 'medium'] / 60) * 100) / 100;
}

/** Ordered for a picker: lightest → densest, with what each typically means. */
export const COMPLEXITY_OPTIONS: { value: ComplexityFactor; label: string; hint: string }[] = [
  { value: 'very-light', label: 'Very light', hint: 'narrative prose — literature, much of history' },
  { value: 'light', label: 'Light', hint: 'descriptive, light recall — much of biology' },
  { value: 'medium', label: 'Medium', hint: 'the default' },
  { value: 'dense', label: 'Dense', hint: 'derivations and worked problems — most physics/maths' },
  { value: 'very-dense', label: 'Very dense', hint: 'heavy problem burden — identities, mensuration' },
];

/** Teacher scope — constrains lists to the subjects/grades they teach. */
export interface CurriculumScope {
  subjects?: string[];
  grades?: string[];
}
